/**
 * FestivalVenuePinPicker
 * ──────────────────────
 * Live map picker for festival/event venues. Drop a pin anywhere on
 * Dominica (or drag the existing one) to set the exact coordinate
 * where the in-app "pocket of light" glow will render.
 *
 * Why this exists
 * ───────────────
 * The festival editor previously asked operators to type a latitude
 * and longitude into two text fields. That works for engineers but
 * not for the sales / ops team — they want to pick the venue on a
 * map the way the campaign wizard already lets them place sponsored
 * map pins. This component is the festival counterpart to the
 * `DraggableLeafletPin` inside `wizard/LivePreview.tsx`.
 *
 * Design parity with the mobile glow
 * ──────────────────────────────────
 * The pin styling mirrors the mobile FestivalGlowMarker:
 *   • circular badge in the festival's `theme_color`
 *   • emoji centred inside (defaults to 🎉)
 *   • soft halo circle at the same radius the mobile app uses
 *     (≈360m) so the operator gets a WYSIWYG sense of how big the
 *     glow will read at typical zoom
 * If you change the radius / shadow in FestivalGlowMarker on the
 * mobile side, update HALO_RADIUS_M here too so admin previews
 * stay honest.
 *
 * UX affordances
 * ──────────────
 *   • Drag the pin OR click anywhere on the map to move it.
 *   • "Recenter on Dominica" button — fast escape hatch when the
 *     operator zooms into the Caribbean Sea by accident.
 *   • Lat/lng readout below the map updates in real-time and is
 *     copy-pasteable.
 *
 * Stack choice
 * ────────────
 * Leaflet + OpenStreetMap. Matches the campaign wizard for
 * consistency (operators learn one map interaction model). Also
 * avoids the Google Maps Cloud Map ID requirement for draggable
 * AdvancedMarkerElements — a known headache the wizard team
 * already routed around by going with Leaflet.
 */

import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { MapPin, Crosshair, Search, Loader2, X } from 'lucide-react';

// ── Tunables ──────────────────────────────────────────────────────────────

// Default centre when no lat/lng has been picked yet — Dominica's
// approximate geographic centre. Matches DOMINICA_CENTER on the
// mobile side.
const DOMINICA_CENTER: [number, number] = [15.4150, -61.3710];

// Default zoom for "I haven't picked anything yet" view. 10 shows
// the whole island; 13 shows a single town. We start at 10 so the
// operator sees context, then zoom in when they click.
const DEFAULT_ZOOM_NO_PICK = 10;
const DEFAULT_ZOOM_WITH_PICK = 14;

// Halo radius in meters. Mirrors the mobile FestivalGlowMarker's
// BASE_HALO_RADIUS_M so the preview is an honest WYSIWYG. If the
// mobile constant moves, move this too.
const HALO_RADIUS_M = 360;

// ── Geocoder (Nominatim / OpenStreetMap) ─────────────────────────────────
//
// We use Nominatim — the free OpenStreetMap geocoder — because:
//   • Zero additional API cost (Google Geocoding API is $5/1000 calls).
//   • No new API key to provision and rotate.
//   • Matches the OSM tiles already loaded by the picker, so coverage
//     gaps in Dominica are the same on the map and in search results
//     (no "I found it on Google but the pin lands in the wrong spot
//     because OSM doesn't know about that building" mismatch).
//
// Nominatim's free-tier usage policy:
//   • 1 request per second per IP — we debounce 500 ms after the
//     operator stops typing, so a single search burst per query.
//   • Bulk geocoding is forbidden — we never auto-search; the
//     operator types into the field interactively.
//   • An identifying header is appreciated; browsers restrict
//     setting User-Agent directly, so we rely on the Referer that
//     fetch sends automatically (admin.h-plate.com etc.).
//
// We bias every search to Dominica with `countrycodes=dm`. If
// Nominatim has no result for a Dominica venue, the operator can
// still drop the pin by clicking the map directly.
const NOMINATIM_ENDPOINT = 'https://nominatim.openstreetmap.org/search';
const NOMINATIM_DEBOUNCE_MS = 500;
const NOMINATIM_MIN_QUERY_LEN = 3;
const NOMINATIM_MAX_RESULTS = 6;

interface NominatimHit {
  place_id: number;
  display_name: string;
  lat: string; // Nominatim returns lat/lon as strings — parse with parseFloat
  lon: string;
  type?: string;
  class?: string;
  importance?: number;
}

// ── Props ─────────────────────────────────────────────────────────────────

export interface FestivalVenuePinPickerProps {
  /** Current lat (NULL while the operator hasn't picked yet). */
  lat: number | null;
  /** Current lng (NULL while the operator hasn't picked yet). */
  lng: number | null;
  /** Festival theme color hex (#RRGGBB). Drives the pin badge + halo color. */
  themeColor: string;
  /** Festival emoji shown on the pin badge. Defaults to 🎉 if absent. */
  emoji?: string | null;
  /** Pin readout label (defaults to the venue name when set). */
  label?: string | null;
  /**
   * Halo radius in metres, or `null` for no halo. Defaults to the festival
   * glow radius. Callers picking a plain coordinate — a hotel's pickup door,
   * say — pass `null`, since a glow ring there would imply a catchment area
   * that does not exist.
   */
  haloRadiusM?: number | null;
  /** Fires when the operator drags the pin or clicks the map. */
  onChange: (lat: number, lng: number) => void;
}

// ── Component ─────────────────────────────────────────────────────────────

export function FestivalVenuePinPicker({
  lat,
  lng,
  themeColor,
  emoji,
  label,
  haloRadiusM,
  onChange,
}: FestivalVenuePinPickerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<any>(null);
  const markerRef = useRef<any>(null);
  const haloRef = useRef<any>(null);
  const onChangeRef = useRef(onChange);
  const [mapReady, setMapReady] = useState(false);

  // Read through a ref because `placeOrMoveMarker` is called from Leaflet
  // handlers bound once at mount, which would otherwise close over the
  // first value forever.
  const haloRadius = haloRadiusM === undefined ? HALO_RADIUS_M : haloRadiusM;
  const haloRadiusRef = useRef(haloRadius);
  haloRadiusRef.current = haloRadius;

  // Geocoder state. The search field is independent of the parent's
  // "Venue name" input — operators may want to search for a known
  // landmark ("Windsor Park Stadium") even when the official venue
  // name they intend to store is more colloquial ("Stadium Roseau").
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<NominatimHit[]>([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);

  // Keep the callback ref fresh without retriggering map init —
  // Leaflet event handlers close over the FIRST onChange we received,
  // which would otherwise become stale when the parent re-renders
  // with a new closure each turn.
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // Debounced Nominatim search. Fires NOMINATIM_DEBOUNCE_MS after
  // the operator stops typing, only when the query is long enough
  // (avoids "w" → 600 results across all of Dominica). Uses an
  // AbortController so an in-flight request gets cancelled if the
  // operator types more characters before it returns — keeps the
  // last-typed query the one whose results are shown.
  useEffect(() => {
    const trimmed = searchQuery.trim();
    if (trimmed.length < NOMINATIM_MIN_QUERY_LEN) {
      setSearchResults([]);
      setSearchError(null);
      return;
    }

    const controller = new AbortController();
    const timer = setTimeout(async () => {
      setSearching(true);
      setSearchError(null);
      try {
        const url =
          NOMINATIM_ENDPOINT +
          '?' +
          new URLSearchParams({
            q: trimmed,
            format: 'json',
            limit: String(NOMINATIM_MAX_RESULTS),
            addressdetails: '0',
            countrycodes: 'dm', // restrict to Dominica
          }).toString();

        const res = await fetch(url, {
          headers: { Accept: 'application/json' },
          signal: controller.signal,
        });
        if (!res.ok) throw new Error(`Geocoder returned ${res.status}`);
        const data = (await res.json()) as NominatimHit[];
        setSearchResults(Array.isArray(data) ? data : []);
        setSearchOpen(true);
      } catch (err: any) {
        if (err?.name === 'AbortError') return; // newer query in-flight
        console.warn('[FestivalVenuePinPicker] Nominatim search failed', err);
        setSearchError('Search is unavailable. Drop the pin manually instead.');
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, NOMINATIM_DEBOUNCE_MS);

    return () => {
      controller.abort();
      clearTimeout(timer);
    };
  }, [searchQuery]);

  // Pick a Nominatim result → move the pin there. After this the
  // pin remains fully draggable (and the map remains click-to-move)
  // so the operator can fine-tune the spot if Nominatim's coordinate
  // is slightly off (which it often is for venue centres vs. building
  // outlines).
  const onPickSearchResult = (hit: NominatimHit) => {
    const nextLat = parseFloat(hit.lat);
    const nextLng = parseFloat(hit.lon);
    if (!Number.isFinite(nextLat) || !Number.isFinite(nextLng)) return;

    void (async () => {
      const L = await import('leaflet');
      placeOrMoveMarker(L, nextLat, nextLng);
      // Zoom in tighter than the click-to-place default since the
      // operator explicitly picked a named place.
      mapRef.current?.setView([nextLat, nextLng], 16, { animate: true });
      onChangeRef.current(nextLat, nextLng);
    })();

    // Echo the picked label back into the search field so the
    // operator can see what they selected — but close the dropdown
    // immediately so it doesn't cover the map.
    setSearchQuery(hit.display_name);
    setSearchOpen(false);
  };

  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults([]);
    setSearchOpen(false);
    setSearchError(null);
  };

  // Build the pin's HTML once per (themeColor, emoji) change. Kept
  // as a function so we can rebuild it when those props move
  // without unmounting the map.
  const buildIconHtml = () => {
    const safeColor = /^#[0-9A-Fa-f]{6}$/.test(themeColor) ? themeColor : '#7c3aed';
    const safeEmoji = emoji || '🎉';
    // Inline SVG-ish HTML — mirrors the look of the mobile glow
    // badge so the operator sees what the riders will see.
    return `
      <div style="
        display:flex;flex-direction:column;align-items:center;
        filter:drop-shadow(0 6px 12px rgba(0,0,0,0.45));
        cursor:grab;
        user-select:none;
      " aria-label="Festival venue marker">
        <div style="
          position:relative;
          width:42px;height:42px;border-radius:50%;
          background:${safeColor};
          display:flex;align-items:center;justify-content:center;
          box-shadow: 0 0 0 2px ${safeColor}55, 0 6px 20px ${safeColor}66;
          border:1.5px solid #ffffffee;
        ">
          <span style="font-size:22px;line-height:24px;">${safeEmoji}</span>
        </div>
        <div style="
          width:0;height:0;
          border-left:7px solid transparent;border-right:7px solid transparent;
          border-top:10px solid ${safeColor};
          margin-top:-2px;
        "></div>
      </div>
    `;
  };

  // Initial map mount. We deliberately depend on [] (mount once)
  // — the marker and halo are mutated in subsequent effects when
  // props change, so we never rebuild the underlying Leaflet
  // instance just because the operator tweaked the theme colour.
  useEffect(() => {
    let cancelled = false;
    const container = containerRef.current;
    if (!container) return;

    const resizeObserver = new ResizeObserver(() => {
      mapRef.current?.invalidateSize();
    });

    void (async () => {
      const L = await import('leaflet');
      await import('leaflet/dist/leaflet.css');
      if (cancelled || !containerRef.current) return;

      const initialCenter: [number, number] =
        lat != null && lng != null ? [lat, lng] : DOMINICA_CENTER;
      const initialZoom =
        lat != null && lng != null ? DEFAULT_ZOOM_WITH_PICK : DEFAULT_ZOOM_NO_PICK;

      const map = L.map(containerRef.current, {
        center: initialCenter,
        zoom: initialZoom,
        // Every picker lives inside a dialog that is taller than the viewport.
        // With wheel zoom on, scrolling with the cursor over the map zooms it
        // instead of scrolling the form, so the operator gets stuck partway
        // down and cannot reach the buttons. Zoom via the +/- control, the
        // double-click, or a trackpad pinch, all of which still work.
        scrollWheelZoom: false,
        zoomControl: true,
        attributionControl: true,
      });

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; OpenStreetMap',
        maxZoom: 19,
      }).addTo(map);

      mapRef.current = map;

      // Map-click → place / move pin. Faster than drag for first-
      // time placement: operator can just click on Windsor Park
      // and we drop the pin there.
      map.on('click', (e: any) => {
        const { lat: clickLat, lng: clickLng } = e.latlng;
        const rounded = {
          lat: Math.round(clickLat * 1e6) / 1e6,
          lng: Math.round(clickLng * 1e6) / 1e6,
        };
        // Place the marker + halo immediately for snappy UX
        placeOrMoveMarker(L, rounded.lat, rounded.lng);
        onChangeRef.current(rounded.lat, rounded.lng);
      });

      // If we already have a venue pin, draw it on first paint.
      if (lat != null && lng != null) {
        placeOrMoveMarker(L, lat, lng);
      }

      resizeObserver.observe(containerRef.current);

      // Leaflet sometimes initializes with a 0-height container if
      // the dialog is still animating in. Two delayed invalidateSize
      // calls cover the common animation curves.
      requestAnimationFrame(() => map.invalidateSize());
      setTimeout(() => map.invalidateSize(), 150);
      setTimeout(() => map.invalidateSize(), 400);

      if (!cancelled) setMapReady(true);
    })();

    return () => {
      cancelled = true;
      setMapReady(false);
      resizeObserver.disconnect();
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
        markerRef.current = null;
        haloRef.current = null;
      }
    };
    // We intentionally mount the map exactly once. Subsequent
    // prop changes are handled by the dedicated useEffects below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Helper used by both initial paint, map-click, and the
  // external-sync useEffect. Pulled out to keep the three call
  // sites identical instead of three near-identical inline blocks.
  function placeOrMoveMarker(L: any, nextLat: number, nextLng: number) {
    const map = mapRef.current;
    if (!map) return;
    const safeColor = /^#[0-9A-Fa-f]{6}$/.test(themeColor) ? themeColor : '#7c3aed';

    if (!markerRef.current) {
      const icon = L.divIcon({
        html: buildIconHtml(),
        className: 'festival-venue-pin-divicon',
        iconSize: [42, 56],
        iconAnchor: [21, 56],
      });
      const m = L.marker([nextLat, nextLng], { draggable: true, icon }).addTo(map);
      m.on('dragend', () => {
        const pos = m.getLatLng();
        const rounded = {
          lat: Math.round(pos.lat * 1e6) / 1e6,
          lng: Math.round(pos.lng * 1e6) / 1e6,
        };
        // Move the halo too so it tracks the marker live.
        haloRef.current?.setLatLng([rounded.lat, rounded.lng]);
        onChangeRef.current(rounded.lat, rounded.lng);
      });
      // Also drag the halo as the marker moves (not just on
      // dragend) so the WYSIWYG preview feels alive.
      m.on('drag', () => {
        const pos = m.getLatLng();
        haloRef.current?.setLatLng([pos.lat, pos.lng]);
      });
      markerRef.current = m;
    } else {
      markerRef.current.setLatLng([nextLat, nextLng]);
    }

    if (!haloRadiusRef.current) {
      // No halo wanted — nothing to create or move.
    } else if (!haloRef.current) {
      haloRef.current = L.circle([nextLat, nextLng], {
        radius: haloRadiusRef.current,
        color: `${safeColor}aa`,
        weight: 1,
        fillColor: `${safeColor}33`,
        fillOpacity: 0.4,
        interactive: false,
      }).addTo(map);
    } else {
      haloRef.current.setLatLng([nextLat, nextLng]);
    }

    map.panTo([nextLat, nextLng], { animate: true, duration: 0.4 });
  }

  // Sync external lat/lng edits (operator typed into the readout
  // panel below the map) → marker position. We compare with a small
  // epsilon to avoid an infinite loop with our own onChange.
  useEffect(() => {
    if (!mapReady || lat == null || lng == null) return;
    if (!markerRef.current) {
      // Operator typed values in before clicking the map; instantiate.
      void (async () => {
        const L = await import('leaflet');
        placeOrMoveMarker(L, lat, lng);
      })();
      return;
    }
    const pos = markerRef.current.getLatLng();
    if (Math.abs(pos.lat - lat) > 0.0001 || Math.abs(pos.lng - lng) > 0.0001) {
      markerRef.current.setLatLng([lat, lng]);
      haloRef.current?.setLatLng([lat, lng]);
      mapRef.current?.panTo([lat, lng], { animate: true, duration: 0.4 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lat, lng, mapReady]);

  // Rebuild the pin icon when themeColor / emoji change so the
  // preview always matches the mobile glow's branding.
  useEffect(() => {
    if (!markerRef.current) return;
    void (async () => {
      const L = await import('leaflet');
      const icon = L.divIcon({
        html: buildIconHtml(),
        className: 'festival-venue-pin-divicon',
        iconSize: [42, 56],
        iconAnchor: [21, 56],
      });
      markerRef.current.setIcon(icon);
      // Update the halo colour to match the new theme too.
      const safeColor = /^#[0-9A-Fa-f]{6}$/.test(themeColor) ? themeColor : '#7c3aed';
      if (haloRef.current) {
        haloRef.current.setStyle({
          color: `${safeColor}aa`,
          fillColor: `${safeColor}33`,
        });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [themeColor, emoji]);

  const recenter = () => {
    if (!mapRef.current) return;
    mapRef.current.setView(DOMINICA_CENTER, DEFAULT_ZOOM_NO_PICK, { animate: true });
  };

  const hasPick = lat != null && lng != null;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-xs text-gray-400">
          <MapPin className="w-3.5 h-3.5" />
          <span>
            {hasPick
              ? 'Drag the pin or click elsewhere on the map to move the glow.'
              : 'Search by venue name or click anywhere on Dominica to drop the pin.'}
          </span>
        </div>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={recenter}
          className="h-7 text-xs gap-1"
        >
          <Crosshair className="w-3 h-3" />
          Recenter
        </Button>
      </div>

      {/*
        Venue geocoder. Type a landmark name → debounced Nominatim
        lookup → pick from the dropdown → pin drops at that point.
        After auto-placement the pin remains fully draggable so
        admins can fine-tune the exact spot (Nominatim often pins
        a venue's geometric centre rather than its public entrance,
        and admins know better than the geocoder does).

        The dropdown is `position: absolute` over the map so it can
        cover the map briefly without pushing the dialog layout
        around — closes on pick or on the X button.
      */}
      <div className="relative">
        <div className="relative">
          <Search
            className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none"
            aria-hidden
          />
          <Input
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setSearchOpen(true);
            }}
            onFocus={() => {
              if (searchResults.length > 0) setSearchOpen(true);
            }}
            placeholder="Search a venue (e.g. Windsor Park Stadium)"
            className="bg-gray-900 border-gray-800 text-white pl-9 pr-9"
            aria-label="Search venue name"
            aria-expanded={searchOpen}
            aria-haspopup="listbox"
          />
          {searching ? (
            <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 animate-spin" />
          ) : searchQuery ? (
            <button
              type="button"
              onClick={clearSearch}
              aria-label="Clear search"
              className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center text-gray-500 hover:text-gray-300 rounded"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          ) : null}
        </div>

        {/* Result dropdown. Floating over the map — z-index 30 keeps
            it above Leaflet's internal controls (zoom buttons, etc.). */}
        {searchOpen && (searchResults.length > 0 || searchError) && (
          <div
            role="listbox"
            className="absolute left-0 right-0 top-full mt-1 z-30 bg-gray-900 border border-gray-700 rounded-lg shadow-xl shadow-black/40 max-h-64 overflow-y-auto"
          >
            {searchError ? (
              <div className="px-3 py-2 text-xs text-amber-300">
                {searchError}
              </div>
            ) : (
              searchResults.map((hit) => (
                <button
                  key={hit.place_id}
                  type="button"
                  role="option"
                  onClick={() => onPickSearchResult(hit)}
                  className="w-full text-left px-3 py-2 hover:bg-gray-800 border-b border-gray-800 last:border-b-0 transition-colors"
                >
                  <div className="text-xs text-white font-medium leading-tight">
                    {hit.display_name.split(',')[0]}
                  </div>
                  <div className="text-[11px] text-gray-500 mt-0.5 truncate">
                    {hit.display_name}
                  </div>
                  <div className="text-[10px] text-gray-600 mt-0.5 font-mono">
                    {parseFloat(hit.lat).toFixed(5)}, {parseFloat(hit.lon).toFixed(5)}
                  </div>
                </button>
              ))
            )}
            <div className="px-3 py-1.5 text-[10px] text-gray-600 bg-gray-950 border-t border-gray-800">
              Search powered by OpenStreetMap · Click a result, then drag the pin to fine-tune.
            </div>
          </div>
        )}
      </div>

      <div
        ref={containerRef}
        className="h-80 w-full rounded-lg border border-gray-800 overflow-hidden bg-gray-950"
      />

      {/* Read-only readout. The two text inputs below this picker
          (Latitude / Longitude in the parent dialog) remain editable
          for operators who already have exact coordinates handy. */}
      {hasPick && (
        <div className="flex items-center justify-between text-[11px] text-gray-500 font-mono px-1">
          <span>
            {label ? `📍 ${label} → ` : '📍 '}
            {lat!.toFixed(6)}, {lng!.toFixed(6)}
          </span>
          <a
            href={`https://www.google.com/maps?q=${lat!.toFixed(6)},${lng!.toFixed(6)}`}
            target="_blank"
            rel="noreferrer"
            className="text-emerald-400 hover:text-emerald-300"
          >
            Open in Google Maps ↗
          </a>
        </div>
      )}
    </div>
  );
}

export default FestivalVenuePinPicker;
